/**
 * Снятие inbound-rtp видео-статистики для конкретного трека переговорки.
 *
 * Перебираем все живые PC из peer-connection-registry и вызываем
 * `getStats(track)`. PC, владеющий ресивером для этого трека, вернёт
 * inbound-rtp с нужными полями. Перебор дёшев: PC обычно 1-2 на сессию.
 */
import defaultTo from 'lodash/defaultTo';
import isFinite from 'lodash/isFinite';

import { finiteOrUndefined } from '../../../../../../shared/helpers/finite';
import type { TranscoderInboundSample } from '../types';
import { getLivePeerConnections } from './peer-connection-registry';

async function getStatsForTrack(pc: RTCPeerConnection, track: MediaStreamTrack): Promise<RTCStatsReport | undefined> {
    try {
        return await pc.getStats(track);
    } catch {
        return undefined;
    }
}

type InboundRtpVideo = {
    bytesReceived: number;
    framesDecoded: number;
    framesPerSecond: number | undefined;
    timestampMs: number;
};

function isInboundRtpVideo(entry: RTCStats): entry is RTCInboundRtpStreamStats {
    return entry.type === 'inbound-rtp' && (entry as RTCInboundRtpStreamStats).kind === 'video';
}

function extractInboundRtpVideo(report: RTCStatsReport | undefined): InboundRtpVideo | undefined {
    if (!report) {
        return undefined;
    }

    for (const entry of report.values()) {
        if (!isInboundRtpVideo(entry)) {
            continue;
        }
        return {
            bytesReceived: defaultTo<number>(entry.bytesReceived, 0),
            framesDecoded: defaultTo<number>(entry.framesDecoded, 0),
            framesPerSecond: finiteOrUndefined(entry.framesPerSecond),
            timestampMs: isFinite(entry.timestamp) ? entry.timestamp : Date.now(),
        };
    }
    return undefined;
}

export async function collectTranscoderInboundSample(
    peerId: string,
    track: MediaStreamTrack
): Promise<TranscoderInboundSample | undefined> {
    const pcs = getLivePeerConnections();
    for (const pc of pcs) {
        const report = await getStatsForTrack(pc, track);
        const inbound = extractInboundRtpVideo(report);
        if (inbound) {
            return { peerId, ...inbound };
        }
    }
    return undefined;
}
